import { useTranslation } from "react-i18next";
import { EmailIcon, FacebookIcon, InstagramIcon, LocationIcon, PinterestIcon, WhatsappIcon } from "../../assets/images/icons/socialMedia";
import { whiteTextLogo } from "../../assets/images/logos";
import Newsletter from "./Newsletter";

// ── Store location ────────────────────────────────────────────
// Taken from the "Thunder clos" Google Maps listing.
//
// The pin is the shop's real coordinates. Careful if you ever re-read them from
// a Maps URL: the "@33.58…,-7.6418…" part is the map's viewport centre, not the
// place. The place sits in the !3d / !4d values (…!3d33.5846829!4d-7.6393208),
// and the two are ~230m apart here.
const STORE_ADDRESS = "Maarif, Rue Ibnou Mounir, near Café l’Unik, Casablanca";

const STORE_COORDS = "33.5846829,-7.6393208";

// The shop's own share link — always resolves to the full listing (name,
// photos, reviews, directions), so it survives any URL format changes.
const STORE_MAPS_LINK = "https://maps.app.goo.gl/639ek594ZmzV3t4n7";

// q=lat,lng(Label) drops a named pin. No API key needed for this embed form.
const STORE_MAPS_EMBED = `https://maps.google.com/maps?q=${STORE_COORDS}(Thunder%20clos)&z=17&output=embed`;

export default function Footer() {
  const { t } = useTranslation();

  const socialLinks = [
    // { link: "https://www.facebook.com/", icon: <FacebookIcon /> },
    {
      link: "https://www.instagram.com/thunderclos?igsh=MW1kbnFpOXNyZHg0eQ==",
      icon: <InstagramIcon />,
    },
    // { link: "https://www.pinterest.com/", icon: <PinterestIcon /> },
    { link: "https://wa.me/212774804516", icon: <WhatsappIcon /> },
  ];

  const infoLinks = [
    {
      text: STORE_ADDRESS,
      icon: <span className="mt-1"><LocationIcon /></span>,
      href: STORE_MAPS_LINK,
      external: true,
    },
    { text: "thundermaroc2025@gmail.com", icon: <span className=""><EmailIcon /></span>, href: "mailto:thundermaroc2025@gmail.com" },
  ];

  return (
    <footer className="bg-black text-white pt-10 pb-6">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* === LOGO & SOCIAL === */}
          <div className="order-1 md:order-none">
            <img loading="lazy" src={whiteTextLogo} className="w-40 h-auto" alt="logo" />
            <p className="mt-4 text-gray-300 text-sm leading-relaxed">{t("logo-footer-text")}</p>

            <div className="mt-6 flex flex-row flex-wrap gap-4">
              {socialLinks.map((item, i) => (
                <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" className="transition-transform duration-300 hover:scale-110">
                  {item.icon}
                </a>
              ))}
            </div>
          </div>

          {/* === STORE MAP === */}
          <div className="order-3 md:order-none">
            <h2 className="font-semibold uppercase text-sm mb-3 border-b border-gray-700 pb-1">{t("visit-us")}</h2>

            <a
              href={STORE_MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("open-in-maps")}
              className="footer-map group block overflow-hidden rounded-lg border border-gray-700 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition-colors"
            >
              <div className="relative h-36">
                <iframe
                  src={STORE_MAPS_EMBED}
                  title={t("visit-us")}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="footer-map-frame absolute inset-0 h-full w-full border-0"
                />
                {/* Sits above the (non-interactive) iframe so the whole card is
                    one link and the map can't swallow scrolls on mobile. */}
                <span className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/0" />
              </div>

              <span className="flex items-center justify-between gap-2 bg-white/5 px-3 py-2 text-xs text-gray-300 group-hover:bg-white/10 transition-colors">
                <span className="truncate">{STORE_ADDRESS}</span>
                <span className="shrink-0 text-white/70 group-hover:text-white" aria-hidden="true">↗</span>
              </span>
            </a>
          </div>

          {/* === INFO LINKS (USE infoLinks array) === */}
          <div className="order-4 md:order-none">
            <h2 className="font-semibold uppercase text-sm mb-3 border-b border-gray-700 pb-1">{t("contact-info")}</h2>
            <ul className="space-y-3">
              {infoLinks.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  {item.icon}
                  {item.href ? (
                    <a
                      href={item.href}
                      {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-sm text-gray-300 leading-snug hover:text-white transition-colors"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-300 leading-snug">{item.text}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* === NEWSLETTER === */}
          <div className="order-2 md:order-none">
            <Newsletter t={t} />
          </div>
        </div>

        {/* === COPYRIGHT === */}
        <p className="border-t border-gray-700 mt-10 pt-4 text-center text-xs text-gray-400">{t("copyright")}</p>
      </div>
    </footer>
  );
}
